import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Card } from "../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Plus, Trash2 } from "lucide-react";

export const newQuestion = () => ({
  text: "",
  type: "single",
  options: ["", ""],
  correct: [],
  block: "",
});

/** Controlled editor for the question list of a single exam/tema. Pure over
 * `questions`/`onChange` so it can be reused by both the multi-tema course
 * editor and the single-exam workshop editor. */
export default function ExamQuestionsEditor({ questions, onChange, idPrefix }) {
  const updateQ = (qIdx, patch) => {
    onChange(questions.map((q, j) => (j === qIdx ? { ...q, ...patch } : q)));
  };

  const changeType = (qIdx, type) => {
    const q = questions[qIdx];
    let options = q.options;
    let correct = [];
    if (type === "truefalse") {
      options = ["Verdadero", "Falso"];
    } else if (q.type === "truefalse") {
      options = ["", ""];
    }
    updateQ(qIdx, { type, options, correct });
  };

  const toggleCorrect = (qIdx, optIdx, multi) => {
    const q = questions[qIdx];
    let next;
    if (multi) {
      next = q.correct.includes(optIdx)
        ? q.correct.filter((x) => x !== optIdx)
        : [...q.correct, optIdx];
    } else {
      next = [optIdx];
    }
    updateQ(qIdx, { correct: next });
  };

  const updateOption = (qIdx, optIdx, value) => {
    const opts = [...questions[qIdx].options];
    opts[optIdx] = value;
    updateQ(qIdx, { options: opts });
  };

  const addOption = (qIdx) => {
    const opts = [...questions[qIdx].options, ""];
    updateQ(qIdx, { options: opts });
  };

  const removeOption = (qIdx, optIdx) => {
    const q = questions[qIdx];
    const opts = q.options.filter((_, i) => i !== optIdx);
    const correct = q.correct.filter((c) => c !== optIdx).map((c) => (c > optIdx ? c - 1 : c));
    updateQ(qIdx, { options: opts, correct });
  };

  const addQuestion = () => {
    const lastBlock = questions.length ? questions[questions.length - 1].block || "" : "";
    onChange([...questions, { ...newQuestion(), block: lastBlock }]);
  };

  const removeQuestion = (qIdx) => {
    onChange(questions.filter((_, j) => j !== qIdx));
  };

  return (
    <div className="space-y-3 pt-2">
      {questions.map((q, qIdx) => {
        const prevBlock = qIdx > 0 ? (questions[qIdx - 1].block || "").trim() : null;
        const thisBlock = (q.block || "").trim();
        const showDivider = qIdx === 0 || thisBlock !== prevBlock;
        return (
          <div key={qIdx}>
            {showDivider && (
              <div className="flex items-center gap-3 mt-4 mb-2">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#bcdfd9] to-transparent" />
                <span className="text-[11px] tracking-[0.2em] uppercase text-brand-ink/70 font-mono-data px-2">
                  {thisBlock ? thisBlock : "Sin bloque"}
                </span>
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-[#bcdfd9] to-transparent" />
              </div>
            )}
            <Card className="p-4 border border-gray-200 shadow-none rounded-lg space-y-3 bg-gray-50/30">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <span className="text-xs font-medium text-gray-700 pt-2">Pregunta {qIdx + 1}</span>
                <div className="flex items-end gap-2">
                  <div className="w-48">
                    <Label className="text-xs">Bloque</Label>
                    <Input
                      value={q.block}
                      onChange={(e) => updateQ(qIdx, { block: e.target.value })}
                      placeholder="Ej: Conceptos básicos"
                      data-testid={`q-block-${idPrefix}-${qIdx}`}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeQuestion(qIdx)}
                    disabled={questions.length === 1}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
              <div>
                <Label className="text-xs">Texto</Label>
                <Textarea
                  value={q.text}
                  onChange={(e) => updateQ(qIdx, { text: e.target.value })}
                  data-testid={`q-text-${idPrefix}-${qIdx}`}
                />
              </div>
              <div className="w-56">
                <Label className="text-xs">Tipo</Label>
                <Select value={q.type} onValueChange={(v) => changeType(qIdx, v)}>
                  <SelectTrigger data-testid={`q-type-${idPrefix}-${qIdx}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="single">Opción única</SelectItem>
                    <SelectItem value="multiple">Opciones múltiples</SelectItem>
                    <SelectItem value="truefalse">Verdadero / Falso</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label className="text-xs">Opciones (marca la/las correctas)</Label>
                {q.type === "single" ? (
                  <RadioGroup
                    value={q.correct[0]?.toString() ?? ""}
                    onValueChange={(v) => toggleCorrect(qIdx, Number(v), false)}
                  >
                    {q.options.map((opt, oi) => (
                      <div key={oi} className="flex items-center gap-2">
                        <RadioGroupItem value={oi.toString()} id={`r-${idPrefix}-${qIdx}-${oi}`} />
                        <Input
                          value={opt}
                          onChange={(e) => updateOption(qIdx, oi, e.target.value)}
                          className="flex-1"
                        />
                        {q.type !== "truefalse" && q.options.length > 2 && (
                          <Button variant="ghost" size="sm" onClick={() => removeOption(qIdx, oi)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </RadioGroup>
                ) : (
                  q.options.map((opt, oi) => (
                    <div key={oi} className="flex items-center gap-2">
                      <Checkbox
                        checked={q.correct.includes(oi)}
                        onCheckedChange={() => toggleCorrect(qIdx, oi, true)}
                      />
                      <Input
                        value={opt}
                        onChange={(e) => updateOption(qIdx, oi, e.target.value)}
                        className="flex-1"
                        disabled={q.type === "truefalse"}
                      />
                      {q.type !== "truefalse" && q.options.length > 2 && (
                        <Button variant="ghost" size="sm" onClick={() => removeOption(qIdx, oi)}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  ))
                )}
                {q.type !== "truefalse" && (
                  <Button variant="outline" size="sm" onClick={() => addOption(qIdx)}>
                    <Plus className="w-3.5 h-3.5 mr-1" /> Añadir opción
                  </Button>
                )}
              </div>
            </Card>
          </div>
        );
      })}
      <Button variant="outline" size="sm" onClick={addQuestion} data-testid={`add-question-${idPrefix}`}>
        <Plus className="w-4 h-4 mr-2" /> Añadir pregunta
      </Button>
    </div>
  );
}
